import { getUploader } from "@lisb/direct-bridge";
import type { AndroidRequestOptions } from "@lisb/direct-bridge/types/uploader/android";
import type { iOSRequestOptions } from "@lisb/direct-bridge/types/uploader/ios";
import sentry from "@/sentry";

type DirectPlatform = "android" | "ios";

/** アップロード先（通知をタップしたときに開く画面）を表すパス */
export type UploadTarget = {
  domainId: string;
  topicId: string;
  messageId?: string;
  commentId?: string;
};

/**
 * direct アプリの中で動いているかを判定する（dxflow-3331）。
 * ナレッジ動画（dx-flow-video）の `CommonUtils.detectPlatform()` と同じ判定方法。
 * URL パラメータ（device）や、アップロード用ハンドラの有無は見ない。
 */
function detectDirectPlatform(): DirectPlatform | undefined {
  if (window.directJsApi?.closeWindow !== undefined) return "android";
  if (window.webkit?.messageHandlers?.DANHCloseWebView) return "ios";
  return undefined;
}

/** 通知をタップしたときに開く URL（掲示板の URL コピーと同じ形） */
function buildNotificationUrl(target: UploadTarget): string {
  const path = [
    target.domainId,
    target.topicId,
    target.messageId,
    target.commentId,
  ]
    .filter(Boolean)
    .join("/");
  return `${window.location.origin}/${path}`;
}

/**
 * direct-bridge の `upload()` に渡すオプションを組み立てる。
 * ナレッジ動画の `createRequestOptions()` と同じ構成（送信方式は既定のまま）。
 */
function createUploadOptions(
  platform: DirectPlatform,
  fileName: string,
  notificationUrl: string,
): iOSRequestOptions | AndroidRequestOptions {
  const errorNotification = {
    title: "アップロード失敗",
    message: `${fileName} のアップロードに失敗しました。`,
    url: notificationUrl,
  };
  if (platform === "ios") {
    return { allowsCellularAccess: true, errorNotification };
  }
  return {
    allowsCellularAccess: true,
    progressNotification: {
      title: "アップロード中",
      message: `${fileName} をアップロード中です。`,
    },
    pendingNotification: {
      title: "アップロードを保留",
      message: `${fileName} のアップロードを保留しています。`,
    },
    errorNotification,
    completeNotification: {
      title: "アップロード成功",
      message: `${fileName} のアップロードに成功しました。`,
    },
  };
}

/**
 * direct アプリの中であれば、ネイティブにファイルのアップロードを依頼する。完了は待たない。
 * 失敗はネイティブの OS 通知で知らせるため、画面には出さず Sentry に送るだけにする。
 * @param id direct-bridge の ID（S3 のキー）
 * @param signedUrl presigned PUT URL
 * @param file 送るファイル
 * @param fileName 通知に表示するファイル名
 * @param target 通知をタップしたときに開く画面
 * @returns ネイティブに依頼した場合 true。direct アプリの外では何もせず false
 */
export function startBackgroundUpload(
  id: string,
  signedUrl: string,
  file: File,
  fileName: string,
  target: UploadTarget,
): boolean {
  const platform = detectDirectPlatform();
  if (!platform) return false;

  const options = createUploadOptions(
    platform,
    fileName,
    buildNotificationUrl(target),
  );
  // 例外が呼び出し側（投稿の作成）へ伝わらないよう、Promise の中で呼ぶ
  Promise.resolve()
    .then(() => getUploader().upload(id, signedUrl, file, options))
    .catch((err: unknown) => {
      sentry.sendSentryError(
        new Error(
          `directBridgeUpload failed (id=${id}): ${describeError(err)}`,
        ),
      );
    });
  return true;
}

/**
 * Sentry に送るエラーの説明（Error は JSON にすると中身が消えるため message を使う）。
 * ネイティブのエラーに presigned URL が含まれていても署名が残らないよう、X-Amz-* の値を伏せる。
 */
function describeError(err: unknown): string {
  return toText(err).replace(/(X-Amz-[A-Za-z-]+=)[^&\s"']*/g, "$1***");
}

function toText(err: unknown): string {
  if (err instanceof Error) return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}
